import * as Sentry from '@sentry/react-native';
import type { User } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { isAuthErrorMessage } from '../supabase/invokeErrorUtils';
import { isExpectedBusinessError } from './expectedErrors';

export function setSentryUser(user: User | null): void {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username: user.email ?? undefined,
    });
  } else {
    Sentry.setUser(null);
  }
}

export type SentryBreadcrumbLevel = 'info' | 'warning' | 'error' | 'debug';

export function addSentryBreadcrumb(
  category: string,
  message: string,
  level: SentryBreadcrumbLevel = 'info',
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    ...(data ? { data } : {}),
  });
}

type CaptureSentryErrorOptions = Record<string, unknown> & {
  fingerprint?: string[];
  tags?: Record<string, string>;
};

export function captureSentryError(
  error: unknown,
  context?: CaptureSentryErrorOptions,
): void {
  try {
    if (!context) {
      Sentry.captureException(error);
      return;
    }
    const { fingerprint, tags, ...extra } = context;
    Sentry.captureException(error, {
      extra,
      ...(tags && Object.keys(tags).length ? { tags } : {}),
      ...(fingerprint?.length ? { fingerprint } : {}),
    });
  } catch {
    // Reporting must never replace the original failure (tests, SDK stub).
  }
}

/**
 * Helper for `.catch()` on best-effort promises — reports the error to Sentry
 * and returns the fallback value (default `undefined`). Use instead of
 * `.catch(() => undefined)` to avoid silently swallowing errors.
 *
 * @example
 * await healConflicts().catch(captureCatch('sync', 'heal-conflicts'));
 * await foo().catch(captureCatch('pos', 'table-cart-save', false));
 */
export function captureCatch(
  module: string,
  op: string,
): (error: unknown) => undefined;
export function captureCatch<T>(
  module: string,
  op: string,
  fallback: T,
): (error: unknown) => T;
export function captureCatch<T>(
  module: string,
  op: string,
  fallback?: T,
): (error: unknown) => T | undefined {
  return (error: unknown) => {
    captureSentryError(error, { tags: { module, op } });
    return fallback;
  };
}

/**
 * Fallo que el usuario ya vio (toast / picker). Tag `user_visible` para que
 * beforeSend no lo tire como red transitoria (ej. CSP → Failed to fetch).
 */
export function reportUserVisibleFailure(params: {
  module: string;
  op: string;
  error: unknown;
  /** Título del toast / contexto corto. */
  title?: string;
  detail?: string;
  /** Código interno del backend (`error.code`). Va a fingerprint+tag: Sentry agrupa por código, no por mensaje friendly (regla error-mapping). */
  errorCode?: string;
}): void {
  // Supabase/PostgrestError no son instanceof Error — son objetos planos con
  // .message, .code, etc. Sin este fallback, el mensaje original se pierde y
  // Sentry recibe solo el title/detail genérico, impidiendo diagnosticar el
  // root cause (VUELTO-NEGOCIO-2B).
  const fallbackMessage =
    (params.error as { message?: unknown })?.message != null
      ? String((params.error as { message: unknown }).message)
      : [params.title, params.detail].filter(Boolean).join(': ') ||
        `User-visible failure: ${params.module}.${params.op}`;
  const err =
    params.error instanceof Error ? params.error : new Error(fallbackMessage);
  // Siempre capturar el mensaje original en extra para diagnóstico, incluso
  // cuando err ya es un Error (su .message puede ser genérico en algunos paths).
  const originalErrorMessage =
    params.error instanceof Error
      ? params.error.message
      : (params.error as { message?: unknown })?.message != null
        ? String((params.error as { message: unknown }).message)
        : String(params.error);
  // Sesión vencida / not authenticated: el usuario ya vio el toast; no es bug.
  if (
    isAuthErrorMessage(originalErrorMessage) ||
    isAuthErrorMessage(params.title) ||
    isAuthErrorMessage(params.detail) ||
    isAuthErrorMessage(fallbackMessage)
  ) {
    return;
  }
  // Validación de negocio esperada (stock insuficiente, `last_active_branch`,
  // permiso backend, `*ValidationError`): el usuario ya vio el toast friendly —
  // no es bug. VUELTO-NEGOCIO-2T / -39. La red transitoria se sigue reportando
  // acá (user_visible): un fetch roto que el usuario vio sí es diagnóstico.
  if (isExpectedBusinessError(params.error)) {
    return;
  }
  captureSentryError(err, {
    fingerprint: [
      'user_visible',
      params.module,
      params.op,
      ...(params.errorCode ? [params.errorCode] : []),
    ],
    tags: {
      user_visible: 'true',
      module: params.module,
      op: params.op,
      ...(params.errorCode ? { error_code: params.errorCode } : {}),
    },
    title: params.title ?? null,
    detail: params.detail ?? null,
    originalErrorMessage,
  });
}

export function setSentryGlobalTags(): void {
  const version = Constants.expoConfig?.version ?? 'unknown';
  Sentry.setTag('app_version', version);
  Sentry.setTag('platform', Platform.OS);
}

export function setSentryBranchTag(branchId: string | null): void {
  if (branchId) {
    Sentry.setTag('branch_id', branchId);
  } else {
    Sentry.setTag('branch_id', undefined as unknown as string);
  }
}

export function setSentryOrganizationTag(organizationId: string | null): void {
  if (organizationId) {
    Sentry.setTag('organization_id', organizationId);
  } else {
    Sentry.setTag('organization_id', undefined as unknown as string);
  }
}
